import {
  IQuestionnaireAnswer,
  IQuestionnaireQuestion,
  IQuestionnaireCategory,
  IQuestionnaireDetails,
} from 'src/shared/types/Questionnaire';
import DOMPurify from 'dompurify';
import { FallbackPlaceholderText } from 'src/utils/constants/common';

export interface IQuestionRowProps {
  question: IPrintQuestion;
  subQuestionLevel?: number;
}

export interface IQuestionListIdMap {
  [caseQuestionnaireId: number]: IPrintCategory[];
}

export interface IQuestionnaireIdMap {
  [caseQuestionnaireId: number]: IQuestionnaireDetails;
}

export interface IPrintQuestion extends IQuestionnaireQuestion {
  questionNo?: number[];
  sections?: IPrintQuestion[][];
}

export interface IPrintCategory extends Omit<IQuestionnaireCategory, 'questions' | 'helpText'> {
  questions: IPrintQuestion[];
  subQuestionLevel?: number;
  helpText?: { categoryHelpText?: string } | string | null;
}

export interface IAnswerMapping {
  answerForQuestion: string;
}

export interface IAnswerGroup {
  answerMapping: Record<string, IAnswerMapping>;
}

const GROUP_TYPES = [
  'FULL_NAME',
  'PHONE_NUMBER',
  'ADDRESS',
  'COUNTRY_STATE',
  'EMAIL_ADDRESS',
  'WORK_LOCATION',
  'TYPE_SPECIFIC_WORK_LOCATION',
  'ADDRESS_MULTI',
];

// Safely sanitize HTML content
export const sanitizeHtml = (htmlContent: string = ''): string => {
  if (!htmlContent) return '';

  return DOMPurify.sanitize(htmlContent.replace(/\n/g, '<br>'), {
    ALLOWED_TAGS: ['br', 'p', 'strong', 'em', 'u', 'b', 'i'],
    ALLOWED_ATTR: [],
  });
};

export const InputFieldConfiguration: Record<string, { numberOfFields: number }> = {
  PHONE_NUMBER: { numberOfFields: 5 },
  ADDRESS: { numberOfFields: 7 },
  FULL_NAME: { numberOfFields: 4 },
  COUNTRY_STATE: { numberOfFields: 2 },
  WORK_LOCATION: { numberOfFields: 9 },
  TYPE_SPECIFIC_WORK_LOCATION: { numberOfFields: 13 },
  EMAIL_ADDRESS: { numberOfFields: 2 },
  ADDRESS_MULTI: { numberOfFields: 7 },
};

export const AddressPlaceholders = [
  'WORK_LOCATION',
  'TYPE_SPECIFIC_WORK_LOCATION',
  'ADDRESS',
  'PHONE_NUMBER',
  'ADDRESS_MULTI',
];

export const GroupedTypes = [
  'PHONE_NUMBER',
  'EMAIL_ADDRESS',
  'WORK_LOCATION',
  'TYPE_SPECIFIC_WORK_LOCATION',
  'ADDRESS',
  'ADDRESS_MULTI',
  'COUNTRY_STATE',
];

export class QuestionnairePrintViewService {
  static isDateType(type: string): boolean {
    return type === 'DATE_PERIOD' || type === 'SINGLE_DATE';
  }

  static isCountryType(type: string): boolean {
    return type === 'COUNTRY' || type === 'COUNTRY_MULTI';
  }

  static isGroupType(type: string): boolean {
    return GROUP_TYPES.includes(type);
  }

  static isEntityType(type: string): boolean {
    return type === 'COMPANY_ENTITY';
  }

  static getEntityName(id: string | number, companyEntities: any[]): string {
    const entityId = Number(id);
    const currentEntity = companyEntities?.find((entity) => entity.companyEntityId === entityId);
    return currentEntity?.entityName || '';
  }

  static checkEmptyAnswer(question: IPrintQuestion): boolean {
    return (
      !question.answers.length &&
      ((this.isGroupType(question.questionInputField) &&
        question.questionInputField !== 'FULL_NAME' &&
        question.questionInputField !== 'COUNTRY_STATE') ||
        !this.isGroupType(question.questionInputField))
    );
  }

  static initConditionalQuestions(question: IPrintQuestion, answerList: IQuestionnaireAnswer[]): IPrintQuestion[][] {
    const sectionGroups: IPrintQuestion[][] = [];
    if (question.answers[0]?.caseAnswerId) {
      const answerGroup = answerList
        .filter((answer) => answer.parentCaseAnswerId === question.answers[0].caseAnswerId)
        .reduce((acc, answer) => {
          const group = answer.sectionGroup || 'default';
          if (!acc[group]) acc[group] = [];
          acc[group].push(answer);
          return acc;
        }, {} as Record<string, IQuestionnaireAnswer[]>);

      const conditionalQuestions =
        question.answerList?.find((answer) => answer.answerId === question.answers[0].answerId)?.conditionalQuestions ||
        [];

      Object.entries(answerGroup).forEach(([_, answers]) => {
        const subAnswerGroup = answers.reduce((acc, answer) => {
          if (!acc[answer.questionId]) acc[answer.questionId] = [];
          acc[answer.questionId].push(answer);
          return acc;
        }, {} as Record<number, IQuestionnaireAnswer[]>);

        const conditionalQuestionsCopy = JSON.parse(JSON.stringify(conditionalQuestions));
        conditionalQuestionsCopy.forEach((subQuestion: IPrintQuestion) => {
          subQuestion.answers = subAnswerGroup[subQuestion.questionId] || [];
          subQuestion.sections = this.initConditionalQuestions(subQuestion, answerList);
        });
        sectionGroups.push(conditionalQuestionsCopy);
      });

      if (!sectionGroups.length && conditionalQuestions.length) {
        const conditionalQuestionsCopy = JSON.parse(JSON.stringify(conditionalQuestions));
        conditionalQuestionsCopy.forEach((subQuestion: IPrintQuestion) => {
          subQuestion.answers = [];
          subQuestion.sections = [];
        });
        sectionGroups.push(conditionalQuestionsCopy);
      }
    }
    return sectionGroups;
  }

  static flattenSubQuestion(question: IPrintQuestion, questionNos: number[]): IPrintQuestion[] {
    const displayList: IPrintQuestion[] = [];

    if (question.isConditional) {
      const conditionalQuestion = { ...question };
      conditionalQuestion.questionNo = [...questionNos];
      questionNos[questionNos.length - 1]++;
      conditionalQuestion.answerList = [];
      conditionalQuestion.sections = [];
      displayList.push(conditionalQuestion);

      if (question.sections && question.sections.length) {
        const subQuestionNos = [...conditionalQuestion.questionNo!, 1];
        question.sections.reverse().forEach((subQuestions: IPrintQuestion[]) => {
          subQuestions.forEach((subQuestion: IPrintQuestion) => {
            const subDisplayQuestion = { ...subQuestion };
            const subDisplayList = this.flattenSubQuestion(subDisplayQuestion, subQuestionNos);
            displayList.push(...subDisplayList);
          });
        });
      }
    } else if (question.isSection) {
      const displaySection = { ...question };
      displaySection.sections = [];
      question.sections?.forEach((sectionQuestions: IPrintQuestion[]) => {
        const displaySectionQuestions: IPrintQuestion[] = [];
        sectionQuestions.forEach((sectionQuestion: IPrintQuestion) => {
          const sectionDisplayQuestion = { ...sectionQuestion };
          sectionDisplayQuestion.questionNo = [...questionNos];
          const subDisplayList = this.flattenSubQuestion(sectionDisplayQuestion, questionNos);
          displaySectionQuestions.push(...subDisplayList);
        });
        displaySection.sections!.push(displaySectionQuestions);
      });
      displayList.push(displaySection);
    } else {
      const displayQuestion = { ...question };
      displayQuestion.questionNo = [...questionNos];
      questionNos[questionNos.length - 1]++;
      displayList.push(displayQuestion);
    }

    return displayList;
  }

  static flattenQuestion(questionList: IPrintCategory[]): IPrintCategory[] {
    const displayList: IPrintCategory[] = [];
    const questionNos = [1];
    let subQuestionLevel = 1;

    questionList.forEach((category) => {
      const displayCategory = { ...category, questions: [] as IPrintQuestion[], subQuestionLevel: 1 };
      displayList.push(displayCategory);
      subQuestionLevel = 1;

      category.questions.forEach((question) => {
        const questionAndSubQuestionList = this.flattenSubQuestion(question as IPrintQuestion, questionNos);
        displayCategory.questions.push(...questionAndSubQuestionList);
      });
      displayCategory.subQuestionLevel = subQuestionLevel;
    });

    return displayList;
  }

  static processQuestionnaireData(
    questionList: IQuestionnaireCategory[],
    answerList: IQuestionnaireAnswer[]
  ): IPrintCategory[] {
    const printQuestionList: IPrintCategory[] = questionList.map((category) => ({
      ...category,
      questions: category.questions.map((q) => ({ ...q } as IPrintQuestion)),
      helpText: typeof category.helpText === 'string' ? { categoryHelpText: category.helpText } : category.helpText,
    }));

    printQuestionList.forEach((category) => {
      category.questions.forEach((question) => {
        const answers = answerList.filter((answer) => answer.questionId === question.questionId);

        if (question.isConditional) {
          question.answers = answers;
          question.sections = this.initConditionalQuestions(question, answerList);
        } else if (question.isSection) {
          const sectionGroups: IPrintQuestion[][] = [];
          const questionIdList = question.sectionQuestions?.map((q) => q.questionId) || [];
          const relevantAnswers = answerList.filter((answer) => questionIdList.includes(answer.questionId));
          const answerGroup = relevantAnswers.reduce((acc, answer) => {
            const group = answer.sectionGroup || 'default';
            if (!acc[group]) acc[group] = [];
            acc[group].push(answer);
            return acc;
          }, {} as Record<string, IQuestionnaireAnswer[]>);

          Object.entries(answerGroup).forEach(([_, answers]) => {
            const subQuestions = JSON.parse(JSON.stringify(question.sectionQuestions));
            const subAnswers = answers.reduce((acc, answer) => {
              if (!acc[answer.questionId]) acc[answer.questionId] = [];
              acc[answer.questionId].push(answer);
              return acc;
            }, {} as Record<number, IQuestionnaireAnswer[]>);

            subQuestions.forEach((subQuestion: IPrintQuestion) => {
              if (subAnswers[subQuestion.questionId]) {
                subQuestion.answers = subAnswers[subQuestion.questionId];
              } else {
                subQuestion.answers = [];
              }
            });
            sectionGroups.push(subQuestions);
          });

          if (!sectionGroups.length) {
            const subQuestions = JSON.parse(JSON.stringify(question.sectionQuestions));
            subQuestions.forEach((subQuestion: IPrintQuestion) => {
              subQuestion.answers = [];
            });
            sectionGroups.push(subQuestions);
          }
          question.sections = sectionGroups;
        } else {
          question.answers = answers;
        }
      });
    });

    return this.flattenQuestion(printQuestionList);
  }

  static parseQuestionnaireIdList(params: { questionnaireId?: string; caseQuestionnaireIdList?: string }) {
    const questionnaireId = params.questionnaireId ? parseInt(params.questionnaireId, 10) : undefined;

    if (params.caseQuestionnaireIdList) {
      try {
        return JSON.parse(params.caseQuestionnaireIdList);
      } catch {
        return [];
      }
    }
    return questionnaireId ? [questionnaireId] : [];
  }

  static getAnswerMapping = (existingAnswers: any[], numberOfAnswers: number, questionInputField: string) => {
    const answerMapping: Record<string, any> = {};

    for (let i = 0; i < numberOfAnswers; i++) {
      const currentAnswer = existingAnswers.find((answer) => answer.answerIndex === i);
      if (currentAnswer) {
        answerMapping[String(i)] = currentAnswer;
      } else {
        const placeholderAnswer = AddressPlaceholders.includes(questionInputField)
          ? ''
          : FallbackPlaceholderText.TripleDash;
        answerMapping[String(i)] = { answerForQuestion: placeholderAnswer, answerIndex: i };
      }
    }

    return answerMapping;
  };

  static getAnswerGroups = (answers: any[], numberOfAnswers: number, questionInputField: string) => {
    const groupedAnswers = answers
      .sort((a, b) => (a.answerGroup || 0) - (b.answerGroup || 0))
      .reduce((groups: Record<string, any[]>, answer) => {
        const group = String(answer.answerGroup || 0);
        if (!groups[group]) groups[group] = [];
        groups[group].push(answer);
        return groups;
      }, {});

    const answerGroups: { answerMapping: Record<string, any> }[] = [];

    Object.values(groupedAnswers).forEach((answers) => {
      const answerGroup = {
        answerMapping: this.getAnswerMapping(answers, numberOfAnswers, questionInputField),
      };
      answerGroups.push(answerGroup);
    });

    return answerGroups;
  };
}
